/* eslint-disable react-refresh/only-export-components -- exposes hook and provider from shared context module */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import i18next from 'i18next';
import { useAuth } from '@/contexts/AuthContext';
import { trpc } from '@/lib/trpc';
import { useToast } from '@/hooks/use-toast';
import { isSupportedLang, type SupportedLang } from '@/lib/i18n';

interface LanguageContextType {
  language: SupportedLang;
  setLanguage: (lang: SupportedLang) => Promise<void>;
  isChanging: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function setHtmlLang(lang: SupportedLang) {
  document.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en';
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { profile } = useAuth();
  const { toast } = useToast();
  const updateLangMutation = trpc.users.updateLanguagePreference.useMutation();
  // Ref the mutation to avoid recreating setLanguage on every mutation state cycle
  const mutationRef = useRef(updateLangMutation);
  useEffect(() => { mutationRef.current = updateLangMutation; });

  const [language, setLanguageState] = useState<SupportedLang>(() => {
    const stored = localStorage.getItem('threadheads-language');
    return isSupportedLang(stored) ? stored : 'en';
  });
  const [isChanging, setIsChanging] = useState(false);

  // Sync HTML lang attribute whenever language state changes
  useEffect(() => {
    setHtmlLang(language);
  }, [language]);

  // Profile reconciliation: DB wins over localStorage
  useEffect(() => {
    if (profile?.preferred_language && isSupportedLang(profile.preferred_language)) {
      const dbLang = profile.preferred_language;
      const cachedLang = localStorage.getItem('threadheads-language');
      if (dbLang !== cachedLang) {
        i18next.changeLanguage(dbLang);
        localStorage.setItem('threadheads-language', dbLang);
        setLanguageState(dbLang);
      }
    }
  }, [profile?.preferred_language]);

  const setLanguage = useCallback(async (lang: SupportedLang) => {
    if (isChanging || !isSupportedLang(lang)) return;
    const previous = language;
    setIsChanging(true);
    try {
      // Await bundle load before proceeding (Chinese loads on first switch)
      await i18next.changeLanguage(lang);
    } catch {
      // Bundle load failed — rollback everything
      await i18next.changeLanguage(previous).catch(() => {});
      toast({
        title: i18next.t('common:error'),
        description: i18next.t('common:languageSaveFailed'),
        variant: 'destructive',
      });
      setIsChanging(false);
      return;
    }
    // Bundle loaded successfully — update local state (useEffect syncs HTML lang)
    localStorage.setItem('threadheads-language', lang);
    setLanguageState(lang);
    setIsChanging(false);
    // Persist to DB fire-and-forget (only if authenticated)
    if (profile) {
      mutationRef.current.mutateAsync({ language: lang }).catch(() => {
        toast({
          title: i18next.t('common:error'),
          description: i18next.t('common:languageSaveFailed'),
          variant: 'destructive',
        });
      });
    }
  }, [language, toast, profile, isChanging]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, isChanging }}>
      {children}
    </LanguageContext.Provider>
  );
}
